// src/components/common/Footer.js
import React from 'react';
import { Link } from 'react-router-dom';
import { Facebook, Instagram, Music2, Youtube } from 'lucide-react';

const footerSections = [
  {
    title: 'Start',
    links: [
      { to: '/register', label: 'Start listening' },
      { to: '/pricing', label: 'Free beta' },
      { to: '/requests', label: 'Request a topic' }
    ]
  },
  {
    title: 'Company',
    links: [
      { to: '/about', label: 'About Us' },
      { to: '/how-it-works', label: 'How It Works' },
      { to: '/contact', label: 'Contact' }
    ]
  },
  {
    title: 'Resources',
    links: [
      { to: '/faq', label: 'FAQ' },
      { to: '/privacy-policy', label: 'Privacy Policy' },
      { to: '/terms-of-service', label: 'Terms of Service' },
      { to: '/cookie-policy', label: 'Cookie Policy' }
    ]
  }
];

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative z-50 border-t border-eduvibe-blue/10 bg-[#020817] py-10 text-white">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_2fr_1fr]">
          <div className="ev-fade-up">
            <Link to="/" aria-label="EduVibz home" className="inline-flex">
              <picture>
                <source srcSet="/EduVibzLogo-square_small.avif" type="image/avif" />
                <source srcSet="/EduVibzLogo-square_small.webp" type="image/webp" />
                <img
                  src="/EduVibzLogo-square_small.png"
                  alt="EduVibz"
                  width="216"
                  height="216"
                  className="h-14 w-auto"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-white/70">
              Curriculum-aligned study songs that make learning feel alive.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 md:grid-cols-3">
            {footerSections.map((section) => (
              <div key={section.title} className="ev-fade-up ev-delay-1">
                <h3 className="text-sm font-extrabold uppercase text-eduvibe-green">{section.title}</h3>
                <ul className="mt-4 space-y-2 text-sm text-white/75">
                  {section.links.map((link) => (
                    <li key={link.to}>
                      <Link to={link.to} className="transition duration-300 hover:text-eduvibe-green">
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="ev-fade-up ev-delay-2">
            <h3 className="text-sm font-extrabold uppercase text-eduvibe-green">Stay in tune</h3>
            <p className="mt-4 text-sm text-white/70">Choose a subject, press play, and make the next study session easier to start.</p>
            <Link to="/register" className="mt-4 inline-flex rounded-full bg-eduvibe-green px-5 py-2.5 text-sm font-extrabold text-slate-950 hover:bg-eduvibe-blue">
              Start listening
            </Link>
            <div className="mt-5 flex gap-3 text-eduvibe-green">
              {[Facebook, Instagram, Youtube, Music2].map((Icon, index) => (
                <span key={index} className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-eduvibe-blue/20 bg-white/5">
                  <Icon className="h-4 w-4" aria-hidden="true" />
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-6 text-xs text-white/55 md:flex-row md:items-center md:justify-between">
          <p>&copy; {currentYear} EduVibz. All rights reserved.</p>
          <p>Study songs for learners who want schoolwork to stick, and adults who want revision to feel less like a fight.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
